// ─────────────────────────────────────────────────────────────────────────────
// Full biometric settings page — every attendance rule is configurable.
// This component ONLY reads/writes the settings collection.
// It NEVER touches BiometricPunch, BiometricEnroll, BiometricDevice, or any
// other operational data. All existing records remain exactly as stored.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';
import {
  Clock, Save, RotateCcw, AlertTriangle, Info,
  ChevronDown, ChevronUp, Settings, Calendar, Fingerprint,
  Shield, Bell, Activity, Timer, Sliders,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Spinner } from '@/shared/components/ui/Spinner';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface BiometricSettings {
  // Office hours
  office_start_time: string;      // 'HH:MM'
  office_end_time: string;        // 'HH:MM'
  crosses_midnight: boolean;
  weekly_offs: number[];          // 0=Sun,1=Mon…6=Sat

  // Check-in rules
  grace_in_minutes: number;       // late arrival grace
  grace_out_minutes: number;      // early departure grace
  half_day_after_minutes: number; // late by this → HD
  absent_after_minutes: number;   // late by this → Absent

  // Check-out / pairing
  checkout_guard_minutes: number; // 2nd punch within this = duplicate
  punch_pairing_mode: 'first_last' | 'positional' | 'state_based';
  // first_last = always 1st punch=in, last punch=out (best for eSSL)
  // positional = old 1st&2nd, 3rd&4th pairing
  // state_based = use device-reported punch_state (IN/OUT buttons)

  // Minimum hours
  min_hours_full_day: number;
  min_hours_half_day: number;

  // Break / deduction
  break_auto_deduct: boolean;
  break_minutes: number;
  break_mode: 'auto_deduct' | 'explicit_punch';
  // auto_deduct = always subtract break_minutes if worked > break threshold
  // explicit_punch = only deduct when device sends break-in/break-out punches
  break_deduct_after_hours: number; // only deduct if worked >= this many hours

  // Overtime
  ot_enabled: boolean;
  ot_starts_after_minutes: number; // OT starts this many min past end_time
  ot_rounding_minutes: number;     // round OT down to nearest N min
  ot_max_hours_per_day: number;
  ot_multiplier: number;
  ot_multiplier_holiday: number;
  ot_multiplier_weekly_off: number;

  // Short leave / WFH
  short_leave_max_minutes: number; // ≤ this late = short leave not HD

  // Device
  auto_approve_devices: boolean;
  device_offline_minutes: number; // after this many min no heartbeat = offline

  // Dedup
  duplicate_window_seconds: number; // identical punch_time within this = dup

  // Attendance
  attendance_auto_recompute: boolean; // recompute on every new punch
  lock_past_months: boolean;          // auto-lock after payroll month closes
}

const DEFAULTS: BiometricSettings = {
  office_start_time: '10:00',
  office_end_time: '19:00',
  crosses_midnight: false,
  weekly_offs: [0],

  grace_in_minutes: 30,
  grace_out_minutes: 10,
  half_day_after_minutes: 30,
  absent_after_minutes: 480,

  checkout_guard_minutes: 30,
  punch_pairing_mode: 'first_last',

  min_hours_full_day: 8,
  min_hours_half_day: 4,

  break_auto_deduct: true,
  break_minutes: 60,
  break_mode: 'auto_deduct',
  break_deduct_after_hours: 6,

  ot_enabled: true,
  ot_starts_after_minutes: 30,
  ot_rounding_minutes: 15,
  ot_max_hours_per_day: 4,
  ot_multiplier: 1,
  ot_multiplier_holiday: 2,
  ot_multiplier_weekly_off: 1.5,

  short_leave_max_minutes: 0,

  auto_approve_devices: false,
  device_offline_minutes: 5,

  duplicate_window_seconds: 60,

  attendance_auto_recompute: true,
  lock_past_months: false,
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function addMinutes(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const wh = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(wh / 60)).padStart(2, '0')}:${String(wh % 60).padStart(2, '0')}`;
}

// ── Section wrapper ───────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Section({ title, description, icon, children, defaultOpen = true }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-outline-variant overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-surface-variant/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
            {icon}
          </span>
          <div>
            <p className="text-sm font-semibold text-on-surface">{title}</p>
            <p className="text-xs text-on-surface-variant">{description}</p>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 shrink-0 text-on-surface-variant" /> : <ChevronDown className="h-4 w-4 shrink-0 text-on-surface-variant" />}
      </button>
      {open && <div className="border-t border-outline-variant/50 p-4 space-y-4">{children}</div>}
    </div>
  );
}

// ── Field components ──────────────────────────────────────────────────────────

interface FieldProps { label: string; hint?: string; children: React.ReactNode; }
function Field({ label, hint, children }: FieldProps) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-on-surface-variant uppercase tracking-wide">{label}</label>
      {children}
      {hint && <p className="text-xs text-on-surface-variant/70 mt-0.5">{hint}</p>}
    </div>
  );
}

const inputCls = 'w-full rounded-md border border-outline-variant bg-surface-dim px-3 py-2 text-sm text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all';
const selectCls = `${inputCls} cursor-pointer`;

// ── Main component ────────────────────────────────────────────────────────────

export function BiometricFullSettingsPanel() {
  const qc = useQueryClient();

  const { data: remote, isLoading, isError } = useQuery({
    queryKey: ['biometric-settings-full'],
    queryFn: async () => {
      const { data } = await api.get<{ settings: BiometricSettings }>('/api/biometric/v2/settings');
      return data.settings;
    },
  });

  const [draft, setDraft] = useState<BiometricSettings>(DEFAULTS);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (remote) {
      setDraft({ ...DEFAULTS, ...remote });
      setDirty(false);
    }
  }, [remote]);

  const set = <K extends keyof BiometricSettings>(k: K, v: BiometricSettings[K]) => {
    setDraft(prev => ({ ...prev, [k]: v }));
    setDirty(true);
  };

  const reset = () => {
    if (remote) { setDraft({ ...DEFAULTS, ...remote }); setDirty(false); }
  };

  const save = useMutation({
    mutationFn: async () => (await api.patch('/api/biometric/v2/settings', draft)).data,
    onSuccess: () => {
      toast.success('Settings saved. New rules apply to future punch processing.');
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['biometric-settings-full'] });
      qc.invalidateQueries({ queryKey: ['biometric-settings'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  if (isLoading) return <div className="flex items-center justify-center p-16"><Spinner /></div>;
  if (isError) return (
    <div className="flex items-center gap-2 rounded-lg bg-danger/10 p-4 text-sm text-danger-text">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      Failed to load settings. Please refresh.
    </div>
  );

  const latestOnTime = addMinutes(draft.office_start_time, draft.grace_in_minutes);
  const halfDayBoundary = addMinutes(draft.office_start_time, draft.half_day_after_minutes);
  const absentBoundary = addMinutes(draft.office_start_time, draft.absent_after_minutes);

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-on-surface">
            <Sliders className="h-5 w-5 text-primary" />
            Biometric Rule Configuration
          </h2>
          <p className="mt-1 text-sm text-on-surface-variant">
            All rules are configurable. Changes only affect how punch records are interpreted — existing
            punch data, enrollments, and device records are never modified.
          </p>
        </div>
        {dirty && (
          <div className="flex items-center gap-1.5 rounded-md bg-warning/10 px-3 py-1.5 text-xs font-medium text-warning-text border border-warning/20">
            <AlertTriangle className="h-3.5 w-3.5" />
            Unsaved changes
          </div>
        )}
      </div>

      {/* Live Summary Card */}
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">Current Rules Preview</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <Pill label="Work window" value={`${draft.office_start_time} – ${draft.office_end_time}`} />
          <Pill label="On-time until" value={latestOnTime} good />
          <Pill label="Half-day after" value={halfDayBoundary} warn />
          <Pill label="Absent after" value={absentBoundary} bad />
        </div>
      </div>

      {/* ── Section 1: Office Hours ─────────────────────────────────────── */}
      <Section title="Office Hours" description="Define the standard work window and days off" icon={<Clock className="h-4 w-4" />}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Work starts" hint="Standard check-in time">
            <input type="time" className={inputCls} value={draft.office_start_time}
              onChange={e => set('office_start_time', e.target.value)} />
          </Field>
          <Field label="Work ends" hint="Standard check-out time">
            <input type="time" className={inputCls} value={draft.office_end_time}
              onChange={e => set('office_end_time', e.target.value)} />
          </Field>
        </div>

        <Field label="Crosses midnight?" hint="Enable for night shifts (e.g. 22:00 – 06:00)">
          <Toggle value={draft.crosses_midnight} onChange={v => set('crosses_midnight', v)} label="Night shift — end time is on the next calendar day" />
        </Field>

        <Field label="Weekly off days" hint="Days when attendance is marked WO (Weekly Off) regardless of punch">
          <div className="flex flex-wrap gap-2 mt-1">
            {DAYS.map((d, i) => (
              <button
                key={d}
                type="button"
                onClick={() => {
                  const curr = draft.weekly_offs ?? [];
                  set('weekly_offs', curr.includes(i) ? curr.filter(x => x !== i) : [...curr, i]);
                }}
                className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                  (draft.weekly_offs ?? []).includes(i)
                    ? 'bg-primary text-on-primary border-primary'
                    : 'border-outline-variant text-on-surface-variant hover:bg-surface-variant'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </Field>
      </Section>

      {/* ── Section 2: Check-in Rules ───────────────────────────────────── */}
      <Section title="Check-in Rules" description="Grace periods, half-day and absent thresholds" icon={<Timer className="h-4 w-4" />}>
        <div className="grid grid-cols-2 gap-4">
          <Field
            label={`Grace period (min)`}
            hint={`On-time window: ${draft.office_start_time} → ${latestOnTime}`}
          >
            <input type="number" min={0} max={180} className={inputCls}
              value={draft.grace_in_minutes}
              onChange={e => set('grace_in_minutes', Number(e.target.value))} />
          </Field>
          <Field label="Early-out grace (min)" hint="Can leave this many min before end_time without penalty">
            <input type="number" min={0} max={60} className={inputCls}
              value={draft.grace_out_minutes}
              onChange={e => set('grace_out_minutes', Number(e.target.value))} />
          </Field>
        </div>

        <div className="rounded-lg bg-surface-dim p-3 text-xs text-on-surface-variant space-y-1 border border-outline-variant/50">
          <p className="font-medium text-on-surface">Threshold Summary</p>
          <p>✅ On time: arrives by <strong>{latestOnTime}</strong></p>
          <p>🟡 Half day: arrives after <strong>{halfDayBoundary}</strong></p>
          <p>🔴 Absent: arrives after <strong>{absentBoundary}</strong></p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Half-day threshold (min past start)"
            hint={`Arrival after ${halfDayBoundary} → HD`}
          >
            <input type="number" min={0} max={480} className={inputCls}
              value={draft.half_day_after_minutes}
              onChange={e => set('half_day_after_minutes', Number(e.target.value))} />
          </Field>
          <Field
            label="Absent threshold (min past start)"
            hint={`Arrival after ${absentBoundary} → Absent`}
          >
            <input type="number" min={0} max={960} className={inputCls}
              value={draft.absent_after_minutes}
              onChange={e => set('absent_after_minutes', Number(e.target.value))} />
          </Field>
        </div>

        <Field label="Short leave max (min)" hint="Late by ≤ this → Short Leave (SL) instead of HD. Set 0 to disable short leave.">
          <input type="number" min={0} max={120} className={inputCls}
            value={draft.short_leave_max_minutes ?? 0}
            onChange={e => set('short_leave_max_minutes', Number(e.target.value))} />
        </Field>
      </Section>

      {/* ── Section 3: Punch Pairing ────────────────────────────────────── */}
      <Section title="Punch Pairing Mode" description="How check-in / check-out pairs are detected from raw punches" icon={<Fingerprint className="h-4 w-4" />}>
        <Field label="Pairing strategy">
          <select className={selectCls} value={draft.punch_pairing_mode}
            onChange={e => set('punch_pairing_mode', e.target.value as BiometricSettings['punch_pairing_mode'])}>
            <option value="first_last">First-in / Last-out (recommended for eSSL)</option>
            <option value="positional">Positional pairs (1st&2nd, 3rd&4th…)</option>
            <option value="state_based">State-based (device reports IN/OUT mode key)</option>
          </select>
        </Field>

        <div className="rounded-lg bg-surface-dim p-3 text-xs text-on-surface-variant border border-outline-variant/50 space-y-1">
          {draft.punch_pairing_mode === 'first_last' && (
            <>
              <p className="font-medium text-on-surface">First-in / Last-out</p>
              <p>Every day: 1st punch = check-in, last punch = check-out. Multiple taps in the morning are ignored — the double-tap bug is automatically fixed.</p>
            </>
          )}
          {draft.punch_pairing_mode === 'positional' && (
            <>
              <p className="font-medium text-on-surface">Positional pairing</p>
              <p>Punch 1&2 = interval 1, Punch 3&4 = interval 2 (for employees who leave and return). A double-tap in the morning can cause a phantom checkout.</p>
            </>
          )}
          {draft.punch_pairing_mode === 'state_based' && (
            <>
              <p className="font-medium text-on-surface">State-based pairing</p>
              <p>Uses the IN/OUT button the employee presses on the device. Requires the device to be programmed with separate IN and OUT function keys.</p>
            </>
          )}
        </div>

        <Field label="Double-tap guard (min)" hint="A 2nd punch within this many minutes of the 1st on the same day is treated as a duplicate check-in (not a checkout). Applies in first_last and positional modes.">
          <input type="number" min={5} max={120} className={inputCls}
            value={draft.checkout_guard_minutes}
            onChange={e => set('checkout_guard_minutes', Number(e.target.value))} />
        </Field>

        <Field label="Raw dedup window (seconds)" hint="Two punches with identical biometric_id within this many seconds of each other are collapsed into one. This happens before pairing.">
          <input type="number" min={10} max={300} className={inputCls}
            value={draft.duplicate_window_seconds}
            onChange={e => set('duplicate_window_seconds', Number(e.target.value))} />
        </Field>
      </Section>

      {/* ── Section 4: Minimum Hours ────────────────────────────────────── */}
      <Section title="Minimum Hours" description="How many hours make a full day vs half day" icon={<Activity className="h-4 w-4" />} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Full day minimum (hrs)" hint="Worked ≥ this → Present (P)">
            <input type="number" min={1} max={16} step={0.5} className={inputCls}
              value={draft.min_hours_full_day}
              onChange={e => set('min_hours_full_day', Number(e.target.value))} />
          </Field>
          <Field label="Half day minimum (hrs)" hint="Worked ≥ this but < full-day minimum → HD">
            <input type="number" min={0.5} max={8} step={0.5} className={inputCls}
              value={draft.min_hours_half_day}
              onChange={e => set('min_hours_half_day', Number(e.target.value))} />
          </Field>
        </div>
      </Section>

      {/* ── Section 5: Break ────────────────────────────────────────────── */}
      <Section title="Break / Lunch" description="Lunch break deduction rules" icon={<Settings className="h-4 w-4" />} defaultOpen={false}>
        <Field label="Break detection mode">
          <select className={selectCls} value={draft.break_mode}
            onChange={e => set('break_mode', e.target.value as BiometricSettings['break_mode'])}>
            <option value="auto_deduct">Auto-deduct fixed break from worked hours</option>
            <option value="explicit_punch">Use device Break-Out / Break-In punches</option>
          </select>
        </Field>

        {draft.break_mode === 'auto_deduct' && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Break duration (min)" hint="Subtracted from total worked hours">
              <input type="number" min={0} max={120} className={inputCls}
                value={draft.break_minutes}
                onChange={e => set('break_minutes', Number(e.target.value))} />
            </Field>
            <Field label="Deduct only if worked ≥ (hrs)" hint="Skip deduction for short shifts">
              <input type="number" min={0} max={12} step={0.5} className={inputCls}
                value={draft.break_deduct_after_hours}
                onChange={e => set('break_deduct_after_hours', Number(e.target.value))} />
            </Field>
          </div>
        )}

        {draft.break_mode === 'explicit_punch' && (
          <div className="rounded-lg bg-surface-dim p-3 text-xs text-on-surface-variant border border-outline-variant/50">
            The eSSL device must be configured with Break-Out (state 2) and Break-In (state 3) function keys. Each break interval is measured exactly.
          </div>
        )}
      </Section>

      {/* ── Section 6: Overtime ─────────────────────────────────────────── */}
      <Section title="Overtime (OT)" description="When and how overtime is calculated" icon={<Bell className="h-4 w-4" />} defaultOpen={false}>
        <Field label="Enable overtime calculation">
          <Toggle value={draft.ot_enabled} onChange={v => set('ot_enabled', v)} label="Calculate OT hours beyond the standard shift" />
        </Field>

        {draft.ot_enabled && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label="OT starts after (min past end)" hint="Buffer before OT starts counting">
                <input type="number" min={0} max={120} className={inputCls}
                  value={draft.ot_starts_after_minutes}
                  onChange={e => set('ot_starts_after_minutes', Number(e.target.value))} />
              </Field>
              <Field label="OT rounding (min)" hint="Round down OT to nearest N minutes. 0 = no rounding.">
                <input type="number" min={0} max={60} className={inputCls}
                  value={draft.ot_rounding_minutes}
                  onChange={e => set('ot_rounding_minutes', Number(e.target.value))} />
              </Field>
              <Field label="OT cap (hrs/day)" hint="Maximum OT credited per day">
                <input type="number" min={0} max={12} step={0.5} className={inputCls}
                  value={draft.ot_max_hours_per_day}
                  onChange={e => set('ot_max_hours_per_day', Number(e.target.value))} />
              </Field>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <Field label="OT multiplier (weekday)" hint="e.g. 1 = straight time, 1.5 = time-and-a-half">
                <input type="number" min={1} max={5} step={0.5} className={inputCls}
                  value={draft.ot_multiplier}
                  onChange={e => set('ot_multiplier', Number(e.target.value))} />
              </Field>
              <Field label="OT multiplier (weekly off)">
                <input type="number" min={1} max={5} step={0.5} className={inputCls}
                  value={draft.ot_multiplier_weekly_off ?? 1.5}
                  onChange={e => set('ot_multiplier_weekly_off', Number(e.target.value))} />
              </Field>
              <Field label="OT multiplier (holiday)">
                <input type="number" min={1} max={5} step={0.5} className={inputCls}
                  value={draft.ot_multiplier_holiday}
                  onChange={e => set('ot_multiplier_holiday', Number(e.target.value))} />
              </Field>
            </div>
          </>
        )}
      </Section>

      {/* ── Section 7: Device ───────────────────────────────────────────── */}
      <Section title="Device Management" description="Device approval and online/offline detection" icon={<Shield className="h-4 w-4" />} defaultOpen={false}>
        <Field label="Auto-approve new devices">
          <Toggle
            value={draft.auto_approve_devices}
            onChange={v => set('auto_approve_devices', v)}
            label="Automatically activate any new device that connects (not recommended — approve manually)"
            danger
          />
        </Field>
        <Field label="Offline threshold (min)" hint="A device with no heartbeat in this many minutes is shown as Offline">
          <input type="number" min={1} max={60} className={inputCls}
            value={draft.device_offline_minutes}
            onChange={e => set('device_offline_minutes', Number(e.target.value))} />
        </Field>
      </Section>

      {/* ── Section 8: System ───────────────────────────────────────────── */}
      <Section title="System Behaviour" description="Recompute and payroll lock settings" icon={<Calendar className="h-4 w-4" />} defaultOpen={false}>
        <Field label="Auto-recompute attendance">
          <Toggle
            value={draft.attendance_auto_recompute}
            onChange={v => set('attendance_auto_recompute', v)}
            label="Automatically recalculate attendance day records after each new punch is received"
          />
        </Field>
        <Field label="Auto-lock past months">
          <Toggle
            value={draft.lock_past_months}
            onChange={v => set('lock_past_months', v)}
            label="Prevent recompute of months older than the current payroll period"
          />
        </Field>
      </Section>

      {/* ── Save bar ────────────────────────────────────────────────────── */}
      <div className="sticky bottom-0 flex items-center justify-between gap-4 rounded-xl border border-outline-variant bg-surface p-4 shadow-lg">
        <div className="flex items-center gap-2 text-xs text-on-surface-variant">
          <Info className="h-3.5 w-3.5" />
          <span>
            Changes apply to new punches and future recomputes. Use <strong>Attendance → Recompute</strong> to
            backfill existing records with the new rules.
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={reset}
            disabled={!dirty}
            className="flex items-center gap-1.5 rounded-md border border-outline-variant px-4 py-2 text-sm text-on-surface-variant hover:bg-surface-variant disabled:opacity-40 transition-colors"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Discard
          </button>
          <button
            type="button"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
            className="flex items-center gap-1.5 rounded-md bg-primary px-5 py-2 text-sm font-medium text-on-primary disabled:opacity-40 hover:opacity-90 transition-opacity"
          >
            {save.isPending
              ? <><Spinner className="h-3.5 w-3.5" /> Saving…</>
              : <><Save className="h-3.5 w-3.5" /> Save rules</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Toggle component ──────────────────────────────────────────────────────────

function Toggle({
  value,
  onChange,
  label,
  danger,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  label: string;
  danger?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-3">
      <div
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`relative h-6 w-11 rounded-full transition-colors ${
          value
            ? danger ? 'bg-danger' : 'bg-primary'
            : 'bg-outline-variant'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
            value ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </div>
      <span className={`text-sm ${value && danger ? 'text-danger-text' : 'text-on-surface'}`}>{label}</span>
    </label>
  );
}

// ── Pill component ────────────────────────────────────────────────────────────

function Pill({ label, value, good, warn, bad }: { label: string; value: string; good?: boolean; warn?: boolean; bad?: boolean }) {
  const color = bad ? 'text-danger-text' : warn ? 'text-warning-text' : good ? 'text-success-text' : 'text-on-surface';
  return (
    <div className="space-y-0.5">
      <p className="text-on-surface-variant">{label}</p>
      <p className={`font-mono font-semibold ${color}`}>{value}</p>
    </div>
  );
}
